import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { CIDADES } from '../../data/cidades';

// Small inline calculator widget for the homepage.
// Full calculator lives at /calculadora.

const TIPOS_APT = [
  { key: 't0', label: 'T0 — estúdio' },
  { key: 't1', label: 'T1' },
  { key: 't2', label: 'T2' },
  { key: 't3', label: 'T3 ou maior' },
];

const fmt = (n) => '€' + Math.round(n).toLocaleString('pt-PT');

const CostCalculatorWidget = () => {
  const [cidade, setCidade] = useState('Lisboa');
  const [pessoas, setPessoas] = useState(1);
  const [apto, setApto] = useState('t1');
  const [loc, setLoc] = useState('c');
  const [carro, setCarro] = useState(false);
  // Track if user has interacted: only then we show the calculated result.
  // On refresh/re-entry the simulator stays blank with default form values.
  const [interacted, setInteracted] = useState(false);
  const touch = () => setInteracted(true);

  const d = CIDADES[cidade];

  const calc = useMemo(() => {
    if (!d) return null;
    const aluguel = d[apto + loc] || d.t1c;
    const ali = Math.round(d.alimentacao * pessoas * 0.85);
    const trans = carro ? 420 : Math.round(d.passe * Math.min(pessoas, 3));
    const fp = pessoas === 1 ? 0.7 : pessoas === 2 ? 1.0 : pessoas <= 4 ? 1.2 : 1.4;
    const util = Math.round(d.util * fp);
    const flaz = pessoas <= 1 ? 1 : pessoas <= 2 ? 1.5 : 2;
    // Use lazer base aproximação: refeicao_barata*4 + cinema (single)
    const lazBase = d.refeicao_barata * 4 + d.cinema;
    const laz = Math.round(lazBase * flaz);
    return { aluguel, ali, trans, util, laz, total: aluguel + ali + trans + util + laz };
  }, [d, apto, loc, pessoas, carro]);

  const isRegional = d?.fonte === 'estimativa_regional';

  const verHref =
    `/calculadora?cidade=${encodeURIComponent(cidade)}` +
    `&pessoas=${pessoas}&apto=${apto}&loc=${loc}&carro=${carro}`;

  return (
    <section
      className="bg-[#F5F0E8] border border-[#D1C9B8] rounded-sm p-5 md:p-6"
      data-testid="cost-widget"
    >
      <p className="text-[11px] font-medium tracking-[0.08em] uppercase text-[#E8652A] mb-1.5">
        Guias · Ferramenta
      </p>
      <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-normal text-[#0F5A34] leading-tight mb-4">
        Quanto custa viver em Portugal?
      </h3>

      <div className="grid grid-cols-2 gap-3">
        {/* Cidade */}
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-[11px] font-medium tracking-[0.06em] uppercase text-[#6B7280]">
            Cidade
          </label>
          <select
            value={cidade}
            onChange={(e) => { setCidade(e.target.value); touch(); }}
            className="w-full px-3 py-2 border border-[#D1C9B8] rounded-sm text-sm bg-white text-[#1A1A1A] appearance-none cursor-pointer focus:outline-none focus:border-[#0F5A34] bg-[length:12px] bg-[right_10px_center] bg-no-repeat pr-8"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")" }}
            data-testid="widget-cidade"
          >
            {Object.keys(CIDADES).sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Pessoas */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium tracking-[0.06em] uppercase text-[#6B7280]">
            Pessoas na casa
          </label>
          <input
            type="number"
            min={1}
            max={8}
            value={pessoas}
            onChange={(e) => { setPessoas(Math.max(1, Math.min(8, parseInt(e.target.value) || 1))); touch(); }}
            className="w-full px-3 py-2 border border-[#D1C9B8] rounded-sm text-sm bg-white text-[#1A1A1A] focus:outline-none focus:border-[#0F5A34]"
            data-testid="widget-pessoas"
          />
        </div>

        {/* Tipo de apartamento */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium tracking-[0.06em] uppercase text-[#6B7280]">
            Tipo de apto.
          </label>
          <select
            value={apto}
            onChange={(e) => { setApto(e.target.value); touch(); }}
            className="w-full px-3 py-2 border border-[#D1C9B8] rounded-sm text-sm bg-white text-[#1A1A1A] appearance-none cursor-pointer focus:outline-none focus:border-[#0F5A34] bg-[length:12px] bg-[right_10px_center] bg-no-repeat pr-8"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")" }}
            data-testid="widget-apto"
          >
            {TIPOS_APT.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>

        {/* Localização */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium tracking-[0.06em] uppercase text-[#6B7280]">
            Localização
          </label>
          <select
            value={loc}
            onChange={(e) => { setLoc(e.target.value); touch(); }}
            className="w-full px-3 py-2 border border-[#D1C9B8] rounded-sm text-sm bg-white text-[#1A1A1A] appearance-none cursor-pointer focus:outline-none focus:border-[#0F5A34] bg-[length:12px] bg-[right_10px_center] bg-no-repeat pr-8"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")" }}
            data-testid="widget-loc"
          >
            <option value="c">Centro</option>
            <option value="p">Periferia</option>
          </select>
        </div>

        {/* Carro toggle */}
        <div className="col-span-2">
          <p className="text-[11px] font-medium tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">
            Pretende ter carro?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setCarro(false); touch(); }}
              className={`flex-1 py-2 text-sm rounded-sm border transition-colors ${!carro ? 'bg-[#0F5A34] border-[#0F5A34] text-white font-medium' : 'bg-white border-[#D1C9B8] text-[#6B7280]'}`}
              data-testid="widget-carro-nao"
            >
              Não — transporte público
            </button>
            <button
              type="button"
              onClick={() => { setCarro(true); touch(); }}
              className={`flex-1 py-2 text-sm rounded-sm border transition-colors ${carro ? 'bg-[#0F5A34] border-[#0F5A34] text-white font-medium' : 'bg-white border-[#D1C9B8] text-[#6B7280]'}`}
              data-testid="widget-carro-sim"
            >
              Sim — carro próprio
            </button>
          </div>
        </div>

        {isRegional && (
          <div className="col-span-2 text-[11px] text-[#6B7280] bg-[#F0EBE0] border border-[#D1C9B8] rounded-sm px-3 py-2">
            Estimativa regional — dados Numbeo não disponíveis individualmente para esta cidade.
          </div>
        )}

        <hr className="col-span-2 border-t border-[#E8E0D0] my-1" />

        {/* Resultado: aparece apenas após o utilizador interagir.
            Em refresh / nova visita, o widget fica em branco até nova simulação. */}
        {calc && interacted && (
          <div className="col-span-2 bg-[#0F5A34] rounded-sm p-4 md:p-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.07em] text-[#8B9E93] font-medium mb-1.5">
                Estimativa mensal
              </div>
              <div className="font-['Playfair_Display'] text-3xl text-[#F5F0E8] leading-none mb-3">
                {fmt(calc.total)}<span className="text-base text-[#8B9E93] font-sans">/mês</span>
              </div>
              <div className="flex flex-col gap-1">
                {[
                  ['Moradia', calc.aluguel],
                  ['Alimentação', calc.ali],
                  ['Transporte', calc.trans],
                  ['Utilities', calc.util],
                  ['Lazer', calc.laz],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 text-xs">
                    <span className="text-[#8B9E93]">{k}</span>
                    <span className="text-[#D1C9B8] font-medium">{fmt(v)}</span>
                  </div>
                ))}
              </div>
            </div>
            <Link
              to={verHref}
              className="flex-shrink-0 bg-[#E8652A] hover:opacity-90 text-white rounded-sm px-3 py-2.5 text-xs font-medium leading-tight text-center inline-flex items-center gap-1 transition-opacity"
              data-testid="widget-ver-completo"
            >
              <span>Ver detalhamento<br/>completo</span>
              <ArrowUpRight size={14} />
            </Link>
          </div>
        )}

        {!interacted && (
          <div className="col-span-2 bg-white border border-dashed border-[#D1C9B8] rounded-sm p-5 text-center">
            <p className="text-sm text-[#6B7280] font-['Inter'] mb-3">
              Preencha os campos acima para ver a sua estimativa personalizada.
            </p>
            <button
              type="button"
              onClick={touch}
              className="inline-flex items-center gap-1.5 bg-[#0F5A34] hover:bg-[#0c4628] text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
              data-testid="widget-calcular-btn"
            >
              Calcular agora
            </button>
          </div>
        )}

        <p className="col-span-2 text-[11px] text-[#6B7280] leading-relaxed mt-1">
          Estimativas baseadas em dados do{' '}
          <a
            href="https://www.numbeo.com/cost-of-living/country_result.jsp?country=Portugal"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#E8652A] no-underline hover:underline"
          >
            Numbeo (jun. 2026)
          </a>
          . Use como referência de planejamento, não como orçamento definitivo.
        </p>
      </div>
    </section>
  );
};

export default CostCalculatorWidget;
